import { useState, useEffect } from 'react'
import { campaignApi } from '../api'
import type { Campaign } from '../types'
import { useNavigate } from 'react-router-dom'

export function CampaignList() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    loadCampaigns()
  }, [])

  const loadCampaigns = async () => {
    try {
      const data = await campaignApi.list()
      setCampaigns(data.campaigns)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (campaign: Campaign) => {
    try {
      if (campaign.active) {
        await campaignApi.deactivate(campaign.id)
      } else {
        await campaignApi.activate(campaign.id)
      }
      await loadCampaigns()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return

    try {
      await campaignApi.delete(id)
      await loadCampaigns()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete campaign')
    }
  }

  if (loading) return <div>Loading campaigns...</div>
  if (error) return <div style={{ color: 'red' }}>{error}</div>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h2>Campaigns</h2>
        <button
          onClick={() => navigate('/campaigns/new')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Create Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <p>No campaigns yet. Create your first campaign to get started!</p>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: campaign.active ? '#f8f9fa' : '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>
                    {campaign.name}
                    {campaign.active && (
                      <span
                        style={{
                          marginLeft: '10px',
                          padding: '2px 8px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}
                      >
                        Active
                      </span>
                    )}
                  </h3>
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    Target: r/{campaign.targetSubreddit}
                  </p>
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    Monitoring: {campaign.monitoredSubreddits.map((s) => `r/${s}`).join(', ')}
                  </p>
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    Keywords: {campaign.keywords.join(', ')}
                  </p>
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    Delay: {campaign.minDelay}s - {campaign.maxDelay}s
                  </p>
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    Max posts/hour: {campaign.maxPostsPerHour}
                  </p>
                  <p style={{ margin: '5px 0', color: '#666' }}>
                    Mode: {campaign.autoApprove ? 'Automatic' : 'Manual Approval'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                    style={{
                      padding: '5px 15px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(campaign)}
                    style={{
                      padding: '5px 15px',
                      backgroundColor: campaign.active ? '#ffc107' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    {campaign.active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(campaign.id)}
                    style={{
                      padding: '5px 15px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
